import {useReactTable, createColumnHelper, flexRender, getCoreRowModel} from '@tanstack/react-table'
import {TestType} from '../types/testType'
import {useEffect, useState, useMemo} from 'react'
import StringInput from './stringInput'
import NumberInput from './numberInput'
import '../product.css'
import Checkbox from '@mui/material/Checkbox'
import useMutationApi from '../../../../../../hooks/useMutation'
import {DELETE_MULTIPLE_TEST, UPDATE_MULTIPLE_TEST} from '../../../GraphQLQueries/ProductOffering'
import {ToastMessageContainer} from '../../../../../../_ePulse/layout/components/ToastMessageContainer'
import {toast} from 'react-toastify'

interface TestTableProps {
  data: TestType[]
  testGroupValue: string
  testGroupLabel: string
  productValue: string
  refetchData: (vars: any) => void
}

export const TestTable = ({data, testGroupValue, testGroupLabel, refetchData}: TestTableProps) => {
  const {createData: UpdateTests} = useMutationApi(UPDATE_MULTIPLE_TEST)
  const {createData: DeleteTests} = useMutationApi(DELETE_MULTIPLE_TEST)

  const [tableData, setTableData] = useState<TestType[]>([])
  const [showSaveButton, setShowSaveButton] = useState(false)

  // Sync internal state when external data changes
  useEffect(() => {
    setTableData(data || [])
    setShowSaveButton(false) // Reset save button on data switch
  }, [data])

  const createNewData = (): TestType => ({
    id: '',
    productName: '',
    testCode: '',
    testGroupName: testGroupValue,
    testName: '',
    testCost: 0,
    isDelete: false,
    testGroupId: '',
    isBusinessCreated: true,
  })

  const columnHelper = createColumnHelper<TestType>()

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => '', {
        id: 'select-col',
        header: () => <span className='text-black'>Select</span>,
        cell: ({row}) =>
          row.original.isBusinessCreated ? (
            <Checkbox
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              onChange={row.getToggleSelectedHandler()}
            />
          ) : null,
      }),
      columnHelper.accessor('testName', {
        header: 'Test Name',
        cell: (info) => <StringInput props={info} placeholder='Test Name' />,
      }),
      columnHelper.accessor('testCost', {
        header: 'Test Cost',
        cell: (info) => <NumberInput props={info} placeholder='Cost' />,
      }),
      columnHelper.accessor('testCode', {
        header: 'Test Code',
        cell: (info) => <StringInput props={info} placeholder='Test Code' />,
      }),
    ],
    [testGroupValue]
  )

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      updateData: (rowIndex: number, columnId: string, value: any) => {
        setShowSaveButton(true)
        setTableData((old) =>
          old.map((row, index) => (index === rowIndex ? {...row, [columnId]: value} : row))
        )
      },
    },
  })

  const selectedRowCount = table.getSelectedRowModel().rows.length
  const hasSelection = selectedRowCount > 0

  const handleAddRow = () => {
    setTableData((prev) => [...prev, createNewData()])
    setShowSaveButton(true)
  }

  const handleDelete = async () => {
    const selectedRows = table.getSelectedRowModel().rows.map((r) => r.original)
    const idsToDelete = selectedRows.map((r) => r.id).filter((id) => id !== '')

    try {
      if (idsToDelete.length > 0) {
        await DeleteTests({testNameIds: idsToDelete})
        toast.success('Tests deleted successfully')
      }
      // Update local state by removing selected rows
      setTableData((prev) => prev.filter((row) => !selectedRows.includes(row)))
      table.resetRowSelection()
      refetchData({productGroup: [testGroupValue]})
    } catch (err) {
      toast.error('Deletion failed')
    }
  }

  const handleUpdate = async () => {
    const payload = tableData
      .filter((t) => t.testName.trim() !== '')
      .map((t) => ({
        id: t.id || '',
        testCode: t.testCode.trim().toUpperCase(),
        testName: t.testName.trim(),
        price: Number(t.testCost),
        testGroupName: testGroupValue,
        testGroupLabel: testGroupLabel,
      }))

    try {
      const res = await UpdateTests({testNameInputs: payload})
      toast.success(res?.data?.updateMultipleTestNames || 'Saved')
      setShowSaveButton(false)
      refetchData({productGroup: [testGroupValue]})
    } catch (err) {
      toast.error('Save failed')
    }
  }

  return (
    <div className='p-4 h-100'>
      {tableData.length > 0 ? (
        <div>
           
            <div className='d-flex justify-content-end mb-3 gap-2'>
              <button className='ePulse-btn-primary' onClick={handleAddRow}>
                Add Test
              </button>
              {hasSelection && (
                <button className='ePulse-btn-primary' onClick={handleDelete}>
                  Delete Selected
                </button>
              )}
            </div>
          

          {tableData.length > 0 ? (
            <div className='admin-table'>
              <table className='table'>
                <thead>
                  {table.getHeaderGroups().map((hg) => (
                    <tr key={hg.id}>
                      {hg.headers.map((header) => (
                        <th key={header.id}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div></div>
          )}

          {showSaveButton && (
            <div className='d-flex justify-content-end mt-3'>
              <button className='ePulse-btn-primary' onClick={handleUpdate}>
                Save Changes
              </button>
            </div>
          )}
        </div>
      ) : (
        <div
          className='d-flex flex-column align-items-center justify-content-center h-100'
          // style={{height: '200px'}}
        >
          <div>No Test associated with this group. Please add tests to show here.</div>
          <button className='ePulse-btn-primary' onClick={handleAddRow}>
            Add Test
          </button>
        </div>
      )}
    </div>
  )
}
